import { merge } from 'lodash';
import { HandbookFieldKind, SourceLocator } from '~/types/AgentActivityLog';
import { mergeNationalAddress, savedSplAddress } from '~/utils/nationalAddress';

export const EXTRA_SUBTREES_BY_PAYLOAD_KEY: Record<string, string[]> = {
  reviewCompanyInformation: ['companyInformation'],
};

export const REVIEW_FIELD_SOURCE_MAP: Record<string, string> = {
  englishLegalName: 'legalName',
  englishCommercialName: 'commercialName',
  englishLegalType: 'legalType',
  englishBeneficiaryFullName: 'accountHolderName',
};

export const REVIEW_PERSON_FIELD_SOURCE_MAP: Record<string, string> = {
  englishFullName: 'fullName',
  englishNationality: 'nationality',
  englishRelation: 'relation',
};

export const SOURCE_LOCATORS: Record<string, SourceLocator> = {
  reviewCompanyInformation: {
    source: (p) => {
      const payload = merge({}, p?.provided, p);
      const company = payload.companyInformation;
      const thiqah = payload.thiqahFullInfo;
      const fiscalYear = thiqah?.fiscalYear;
      const ecommerce = thiqah?.hasEcommerce;
      return {
        ...company,
        commercialName: company?.businessDetails?.merchantNameEnglish ?? company?.commercialName,
        arabicCommercialName: company?.businessDetails?.merchantNameArabic,
        agentCrStatus: thiqah?.status?.name,
        agentConfirmationDate: thiqah?.status?.confirmationDate?.gregorian,
        agentSuspensionDate: thiqah?.status?.suspensionDate?.gregorian,
        agentApplicantPhone: thiqah?.contactInfo?.phoneNo,
        agentMobileNumber: payload.elmInfo?.mobile,
        applicantMobileNumber: payload.applicantDetails?.phoneNumber,
        agentWebsiteUrl: company?.websiteUrl,
        agentIsEcommerce: [true, 'yes', 'Yes'].includes(ecommerce) ? 'yes'
          : [false, 'no', 'No'].includes(ecommerce) ? 'no' : undefined,
        agentCapitalAmount: thiqah?.crCapital,
        agentFiscalYearDayMonth: fiscalYear?.endDay && fiscalYear?.endMonth
          ? `${String(fiscalYear.endDay).padStart(2, '0')}/${String(fiscalYear.endMonth).padStart(2, '0')}`
          : undefined,
        agentCalendarType: fiscalYear?.calendarTypeName,
        nationalAddress: mergeNationalAddress(
          savedSplAddress(payload), payload.reviewCompanyInformation?.nationalAddress,
        ),
      };
    },
    fieldMap: REVIEW_FIELD_SOURCE_MAP,
  },
  reviewBankAccount: {
    source: (p) => ({
      accountHolderName: p?.bankAccount?.accountHolderName ?? null,
      bankName: p?.bankAccount?.bankName ?? null,
      iban: p?.bankAccount?.iban ?? null,
      swiftCode: p?.bankAccount?.swiftCode ?? null,
    }),
    fieldMap: {
      ...REVIEW_FIELD_SOURCE_MAP,
      agentBankName: 'bankName',
      agentIban: 'iban',
      agentSwiftCode: 'swiftCode',
    },
  },
  reviewManagers: {
    fieldMap: {},
    arrays: [
      {
        arrayKey: 'managers',
        sourceArray: (p) => p?.managers?.managers,
        personFieldMap: REVIEW_PERSON_FIELD_SOURCE_MAP,
      },
    ],
  },
  reviewShareholders: {
    fieldMap: {},
    arrays: [
      {
        arrayKey: 'individuals',
        sourceArray: (p) => p?.shareholders?.individuals,
        personFieldMap: REVIEW_PERSON_FIELD_SOURCE_MAP,
      },
      {
        arrayKey: 'legalEntities',
        sourceArray: (p) => p?.shareholders?.legalEntities,
      },
      {
        arrayKey: 'beneficialOwners',
        sourceArray: (p) => p?.shareholders?.beneficialOwners,
        personFieldMap: REVIEW_PERSON_FIELD_SOURCE_MAP,
      },
    ],
  },
  reviewContractReview: {
    source: (p) => p?.contractReview,
    fieldMap: REVIEW_FIELD_SOURCE_MAP,
  },
};

export const REVIEW_PAYLOAD_KEYS = Object.keys(SOURCE_LOCATORS);

export const TRACKED_STEP_KEYS = REVIEW_PAYLOAD_KEYS.map(
  (k) => `review.${k.replace(/^review/, '').replace(/^./, (c) => c.toLowerCase())}`
);

export const HANDBOOK_FIELD_KEYS: Record<string, HandbookFieldKind> = {
  'personalData.englishNationality': 'nationality',
  'personalData.englishRelation': 'relation',
  englishLegalType: 'legalType',
};

export const FIELD_LABELS: Record<string, string> = {
  englishLegalName: 'Company legal name',
  englishCommercialName: 'Company commercial name',
  englishLegalType: 'Company legal type',
  englishBeneficiaryFullName: 'Beneficiary name',
  agentBankName: 'Bank name – Agent Updates',
  derivedBankName: 'Derived Bank Name',
  agentIban: 'IBAN – Agent Updates',
  agentSwiftCode: 'Swift Code – Agent Updates',
  'personalData.englishFullName': 'Full name',
  'personalData.englishNationality': 'Nationality',
  'personalData.englishRelation': 'Relation',
  'companyInformation.mccGroup': 'Main Business Activity',
  'companyInformation.mccCode': 'Business nature (MCC code)',
  'companyInformation.masterId': 'Master MID',
  'companyInformation.terminalId': 'Master TID',
};

export const ARRAY_GROUP_SOURCES: Record<string, (payload: any) => any[] | undefined> = {
  managers: (p) => p?.managers?.managers,
  individuals: (p) => p?.shareholders?.individuals,
  legalEntities: (p) => p?.shareholders?.legalEntities,
  beneficialOwners: (p) => p?.shareholders?.beneficialOwners,
};

export const USER_ROLE_LABELS = {
  nogoManager: 'Nogo Manager',
  agent: 'Agent',
} as const;
